import { find } from "lodash";
import { stringifyUrl } from "query-string";
import React, { useMemo } from "react";
import { useContacts } from "../context/contacts";
import { useMembers } from "../context/members";
import { getMemberAvatarUrl } from "../context/members/utils";
import classNames from "../utils/classNames";
import { avatarsApi } from "../utils/constants";
import DisplayLabels from "./labels/DisplayLabels";

interface Props {
  contactId: string;
  fallbackName?: string;
  subtitle?: any;
  withoutLabels?: boolean;
  withoutDarkMode?: boolean;
}

const ContactComponent: React.FC<Props> = ({
  contactId,
  fallbackName,
  subtitle,
  withoutLabels = false,
  withoutDarkMode = false
}) => {
  const { members } = useMembers();
  const { contacts } = useContacts();

  const contact = useMemo(() => contacts[contactId], [contactId, contacts]);

  const member = useMemo(
    () => (contact?.email ? find(members, { email: contact.email }) : null),
    [contact?.email, members]
  );

  if (!contact && !fallbackName) return null;

  return (
    <>
      <div className="flex-shrink-0 flex justify-center items-center w-8 md:w-10">
        <img
          className="w-8 h-8 md:h-10 md:w-10 rounded-full"
          src={
            member
              ? getMemberAvatarUrl(member, 40)
              : stringifyUrl({
                  url: `${avatarsApi}/human/${contactId}.svg`,
                  query: { w: 40, h: 40 }
                })
          }
          onError={(e: any) => {
            e.target.onerror = null;
            e.target.src = stringifyUrl({
              url: `${avatarsApi}/human/${contactId}.svg`,
              query: { w: 40, h: 40 }
            });
          }}
          alt="Contact avatar"
        />
      </div>
      <div className="flex-1 min-w-0 focus:outline-none">
        <p
          className={classNames(
            "text-xs md:text-sm font-medium text-gray-900 group-hover:underline",
            !withoutDarkMode && "dark:text-white"
          )}
        >
          {contact?.name || fallbackName} {member && `(${member.name})`}
        </p>
        {subtitle}
        {!withoutLabels && (
          <DisplayLabels linkedUuid={contactId} size="small" withoutDarkMode />
        )}
      </div>
    </>
  );
};

export default ContactComponent;
